package gr.tech.site.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.css.functions.min
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.navigation.OpenLinkStrategy
import com.varabyte.kobweb.silk.components.icons.fa.FaCirclePlay
import com.varabyte.kobweb.silk.components.icons.fa.IconSize
import com.varabyte.kobweb.silk.components.layout.HorizontalDivider
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.animation.toAnimation
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.toModifier
import gr.tech.site.components.layouts.PageLayoutData
import gr.tech.site.components.sections.DropdownFadeInAnim
import gr.tech.site.data.PODCAST_LIST
import gr.tech.site.models.PodcastEntry
import gr.tech.site.styles.ButtonHoverDarkGreen
import gr.tech.site.styles.NavLinkHoverStyle
import gr.tech.site.styles.Theme
import gr.tech.site.styles.wideCappedWidth
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

@InitRoute
fun initPodcastPage(ctx: InitRouteContext) {
    ctx.data.add(PageLayoutData(
        showContactHeader = true,
        title = "Podcast | Green Rebates Interviews, Industry Insights & Grower Stories",
        description = "Listen to Green Rebates podcast episodes featuring cannabis operators, energy experts, and rebate strategies for LED grow lights, HVAC, and facility upgrades.",
        canonicalUrl = "https://greenrebates.com/podcast"
    ))
}

@Page
@Layout(".components.layouts.PageLayout")
@Composable
fun PodcastPage() {

    var isPodcastSortSelectorOpen by remember { mutableStateOf(false) }
    var podcastSort by remember { mutableStateOf(PodcastSort.POPULAR) }

    Column(
        modifier = Modifier
            .wideCappedWidth()
            .classNames("mt-5", "mx-auto", "mb-5"),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        H1(
            Modifier.classNames("fs-1", "fw-bold", "w-full", "text-center", "mb-2")
                .toAttrs()
        ) {
            Text("Podcast")
        }

        P(
            Modifier.classNames("fs-5", "text-center", "mb-2")
                .width(min(100.percent, 620.px))
                .color(Theme.DarkGreen.rgb)
                .opacity(0.8)
                .toAttrs()
        ) {
            Text("Listen to Green Rebates podcast episodes featuring cannabis operators, energy experts, and newest rebate news.")
        }

        P(
            Modifier.classNames("fs-6", "text-center", "mb-2", "w-75")
                .color(Theme.DarkGreen.rgb)
                .opacity(0.5)
                .toAttrs()
        ) {
            Text("Want to be a guest? Contact us, and we'll set it up.")
        }

        Link(
            path = SitePage.ContactUs.route,
            text = "BE A GUEST",
            variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
            modifier = ButtonHoverDarkGreen.toModifier()
                .classNames("fs-6", "px-3", "py-2", "mb-3")
                .role("button").attrsModifier {
                    attr("aria-label", "See how Green Rebates works")
                },
            openInternalLinksStrategy = OpenLinkStrategy.IN_PLACE,
            openExternalLinksStrategy = OpenLinkStrategy.IN_PLACE
        )

        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.Start,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            PodcastSortSelector(
                isOpen = isPodcastSortSelectorOpen,
                selected = podcastSort,
                onOpen = { isPodcastSortSelectorOpen = !isPodcastSortSelectorOpen },
                onChange = { sort ->
                    podcastSort = sort
                    isPodcastSortSelectorOpen = false
                }
            )
        }

        HorizontalDivider(modifier = Modifier.classNames("mb-4"))

        Ul(
            attrs = Modifier
                .fillMaxWidth()
                .classNames("row", "row-cols-1", "row-cols-md-2", "row-cols-lg-3", "g-4", "list-unstyled")
                .toAttrs()
        ) {
            when (podcastSort) {
                PodcastSort.POPULAR -> {
                    PODCAST_LIST.getPopular().forEach { entry ->
                        Li(attrs = Modifier.classNames("col").toAttrs()) {
                            PodcastPreviewCard(entry)
                        }
                    }
                }
                PodcastSort.NEWEST -> {
                    PODCAST_LIST.getNewest().forEach { entry ->
                        Li(attrs = Modifier.classNames("col").toAttrs()) {
                            PodcastPreviewCard(entry)
                        }
                    }
                }
            }

        }
    }
}

enum class PodcastSort {
    POPULAR,
    NEWEST;

    override fun toString() = when (this) {
        POPULAR -> "Popular"
        NEWEST -> "Newest"
    }
}

@Composable
fun PodcastSortSelector(
    isOpen: Boolean,
    selected: PodcastSort,
    onOpen: () -> Unit,
    onChange: (PodcastSort) -> Unit
) {
    Div(attrs = Modifier.classNames("dropdown").attrsModifier { onClick { it.stopPropagation() } }.toAttrs()) {
        Link(
            path = "#",
            text = selected.toString(),
            variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
            modifier = NavLinkHoverStyle.toModifier()
                .classNames("fs-6", "p-2", "dropdown-toggle")
                .attrsModifier {
                    attr("aria-expanded", if (isOpen) "true" else "false")
                    onClick { it.preventDefault(); onOpen() }
                }
        )
        Ul(attrs = Modifier
            .classNames("dropdown-menu", "dropdown-menu-light", "mt-2")
            .then(if (isOpen) Modifier.classNames("show").animation(
                DropdownFadeInAnim.toAnimation(duration = 150.ms, timingFunction = AnimationTimingFunction.EaseOut)
            ) else Modifier)
            .toAttrs()
        ) {
            PodcastSort.entries.forEach { sort ->
                Li {
                    Span(
                        attrs = Modifier
                            .color(Theme.DarkGreen.rgb)
                            .classNames("fs-6", "px-3", "py-1", "dropdown-item")
                            .cursor(Cursor.Pointer)
                            .attrsModifier { onClick { onChange(sort); } }
                            .toAttrs()
                    ) {
                        Text(sort.toString())
                    }
                }
            }
        }
    }
}

@Composable
fun PodcastPreviewCard(
    entry: PodcastEntry,
    showShadow: Boolean = true
) {
    Link(
        path = entry.link,
        openExternalLinksStrategy = OpenLinkStrategy.IN_NEW_TAB,
        modifier = Modifier.fillMaxWidth().textDecorationLine(TextDecorationLine.None)
    ) {
        Div(
            attrs = PodcastCardStyle.toModifier()
                .classNames("card", "h-100")
                .then(if (showShadow) Modifier.classNames("shadow") else Modifier)
                .cursor(Cursor.Pointer)
                .toAttrs()
        ) {

            // Thumbnail
            Div(attrs = Modifier.classNames("ratio", "ratio-16x9", "position-relative").backgroundColor(Colors.Black).toAttrs()) {
                Img(
                    src = "https://img.youtube.com/vi/${entry.thumbnailId}/maxresdefault.jpg",
                    alt = "Podcast episode thumbnail",
                    attrs = Modifier
                        .classNames("w-100", "h-100")
                        .objectFit(ObjectFit.Cover)
                        .opacity(0.8)
                        .toAttrs()
                )
                Div(
                    attrs = Modifier
                        .classNames(
                            "position-absolute", "top-0", "start-0",
                            "w-100", "h-100",
                            "d-flex", "justify-content-center", "align-items-center"
                        )
                        .toAttrs()
                ) {
                    FaCirclePlay(
                        modifier = PlayIconStyle.toModifier().classNames("podcast-play-icon"),
                        size = IconSize.X3
                    )
                }
            }

            // Card body
            Div(attrs = Modifier.classNames("card-body", "d-flex", "flex-column").toAttrs()) {
                H2(
                    Modifier
                        .classNames("fs-5", "fw-bold", "mb-1")
                        .color(Theme.DarkGreen.rgb)
                        .toAttrs()
                ) {
                    Text(entry.title)
                }

                P(
                    Modifier
                        .classNames("fs-6", "lh-base", "mb-0", "flex-grow-1")
                        .color(Theme.DarkGreen.rgb)
                        .opacity(0.75)
                        .toAttrs()
                ) {
                    Text(entry.desc)
                }
            }

            // Card footer
            Div(
                attrs = Modifier
                    .classNames("card-footer", "d-flex", "justify-content-between", "align-items-center")
                    .toAttrs()
            ) {
                Span(
                    Modifier
                        .classNames("fs-6", "fw-medium", "text-truncate", "me-2")
                        .color(Theme.MediumGreen.rgb)
                        .toAttrs()
                ) {
                    Text("Green Rebates")
                }
            }
        }
    }
}

val PodcastCardStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .border(1.px, LineStyle.Solid, Theme.MediumGreen.rgb)
            .borderRadius(8.px)
            .overflow(Overflow.Hidden)
            .backgroundColor(Theme.LightGray.rgb)
            .transition(Transition.all(250.ms))
    }
    hover {
        Modifier
            .boxShadow(
                offsetX = 0.px,
                offsetY = 4.px,
                blurRadius = 12.px,
                spreadRadius = 0.px,
                color = rgba(0, 0, 0, 0.1)
            )
            .translateY((-2).px)
    }
    cssRule(":hover .podcast-play-icon") {
        Modifier.color(Theme.MediumGreen.rgb)
    }
}

val PlayIconStyle = CssStyle {
    base {
        Modifier
            .color(Colors.White)
            .transition(Transition.of("color", 250.ms))
    }
}
